<template>
  <TheAdmin title="사용자 추가" description="새로운 사용자를 추가합니다." :color="true">
    <a-row :gutter="16" type="flex">
      <a-col :span="24" class="mt">
        <a-spin tip="로딩 중..." :spinning="isLoading">
          <a-card title="계정 정보">
            <a-form-item
              label="이름:"
              :validate-status="details.username ? 'error' : ''"
              :help="details.username"
            >
              <a-input v-model="account.username" />
            </a-form-item>
            <a-form-item
              label="아이디:"
              :validate-status="details.identity ? 'error' : ''"
              :help="details.identity"
            >
              <a-input v-model="account.identity" />
            </a-form-item>
            <a-row :gutter="16">
              <a-col :md="12" :span="24">
                <a-form-item
                  label="전화번호:"
                  :validate-status="details.phone ? 'error' : ''"
                  :help="details.phone"
                >
                  <a-input @change="getMask('phone')" v-model="account.phone" />
                </a-form-item>
              </a-col>
              <a-col :md="12" :span="24">
                <a-form-item
                  label="이메일:"
                  :validate-status="details.email ? 'error' : ''"
                  :help="details.email"
                >
                  <a-input v-model="account.email" />
                </a-form-item>
              </a-col>
            </a-row>
            <a-form-item
              label="등록 가능한 도매:"
              :validate-status="details.locations ? 'error' : ''"
              :help="details.locations"
            >
              <a-select mode="tags" style="width: 100%" v-model="account.locations">
                <a-select-option
                  v-for="location in locations"
                  :key="location._id"
                  :value="location._id"
                >{{`${location.first} ➡ ${location.secondary}`}}</a-select-option>
              </a-select>
            </a-form-item>
            <p>비밀번호는 전화번호로 전송됩니다.</p>
          </a-card>
        </a-spin>
      </a-col>
      <a-col :span="24" class="mt">
        <a-spin tip="로딩 중..." :spinning="isLoading">
          <a-card :loading="isLoading" title="결제 정보">
            <a-form-item label="이용 방식">
              <a-radio-group v-model="account.balance.paymentType">
                <a-radio :value="0">월정액</a-radio>
                <a-radio :value="1">건당 금액</a-radio>
              </a-radio-group>
            </a-form-item>
            <a-form-item
              label="잔액:"
              :validate-status="details.balance.current ? 'error' : ''"
              :help="details.balance.current"
            >
              <a-input-number
                :formatter="value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')"
                :parser="value => value.replace(/\$\s?|(,*)/g, '')"
                v-model="account.balance.current"
              />
              <span style="margin-left: 5px;">원</span>
            </a-form-item>
            <a-card>
              <a-form-item label="계산">
                <a-input-number
                  :formatter="value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')"
                  :parser="value => value.replace(/\$\s?|(,*)/g, '')"
                  v-model="amountDiff"
                  style="width: 90%; margin-right: 5px;"
                />원
              </a-form-item>
              <a-button-group>
                <a-button type="primary" icon="plus" @click="updateAmountDiff(true)">더하기</a-button>
                <a-button type="danger" icon="minus" @click="updateAmountDiff(false)">빼기</a-button>
              </a-button-group>
            </a-card>
            <a-form-item
              v-if="account.balance.paymentType === 1"
              label="배송료:"
              :validate-status="details.balance.shipping ? 'error' : ''"
              :help="details.balance.shipping"
            >
              <a-input-number
                :formatter="value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')"
                :parser="value => value.replace(/\$\s?|(,*)/g, '')"
                v-model="account.balance.shipping"
              />
              <span style="margin-left: 5px;">원</span>
            </a-form-item>
            <a-form-item
              :label="account.balance.paymentType === 0 ? '월 차감 금액' : '건당 금액'"
              :validate-status="details.balance.price ? 'error' : ''"
              :help="details.balance.price"
            >
              <a-input-number
                :formatter="value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')"
                :parser="value => value.replace(/\$\s?|(,*)/g, '')"
                v-model="account.balance.price"
              />
              <span style="margin-left: 5px;">원</span>
            </a-form-item>
            <a-form-item
              v-if="account.balance.paymentType === 0"
              label="결제 예정일:"
              :validate-status="details.balance.paymentDate ? 'error' : ''"
              :help="details.balance.paymentDate"
            >
              <a-date-picker v-model="account.balance.paymentDate" format="LL" />
            </a-form-item>
            <a-form-item label="접수 시간대">
              <a-card>
                <a-form-item label="항상 접수 가능">
                  <a-switch v-model="account.apply.always" />
                </a-form-item>
                <a-row :gutter="16" v-if="!account.apply.always">
                  <a-col :span="24" :xl="12">
                    <a-form-item label="시작 시간">
                      <a-time-picker
                        use12Hours
                        format="h:mm:ss A"
                        v-model="account.apply.startTime"
                      />
                    </a-form-item>
                  </a-col>
                  <a-col :span="24" :xl="12">
                    <a-form-item label="마감 시간">
                      <a-time-picker use12Hours format="h:mm:ss A" v-model="account.apply.endTime" />
                    </a-form-item>
                  </a-col>
                </a-row>
              </a-card>
            </a-form-item>
          </a-card>
        </a-spin>
      </a-col>
      <a-col :md="24" :span="24" class="mt">
        <a-spin tip="로딩 중..." :spinning="isLoading">
          <a-card title="사업자 정보">
            <a-form-item
              label="이름:"
              :validate-status="details.business.name ? 'error' : ''"
              :help="details.business.name"
            >
              <a-input v-model="account.business.name" />
            </a-form-item>
            <a-form-item
              label="전화번호:"
              :validate-status="details.business.phone ? 'error' : ''"
              :help="details.business.phone"
            >
              <a-input @change="getBusinessMask('phone')" v-model="account.business.phone" />
            </a-form-item>
            <a-form-item
              label="주소:"
              :validate-status="details.business.address ? 'error' : ''"
              :help="details.business.address"
            >
              <a-input v-model="account.business.address" />
            </a-form-item>
            <a-form-item
              label="홈페이지 주소:"
              :validate-status="details.business.homepage ? 'error' : ''"
              :help="details.business.homepage"
            >
              <a-input v-model="account.business.homepage" />
            </a-form-item>
          </a-card>
        </a-spin>
      </a-col>
    </a-row>

    <a-button
      :loading="isLoading"
      @click="onApply"
      class="fl-right mt"
      type="primary"
      icon="plus"
    >추가</a-button>
  </TheAdmin>
</template>
<script>
import TheAdmin from "@/components/Admin/TheAdmin";
import PaymentType from "@/libs/PaymentType";
import config from "../../../config";
import Axios from "axios";
import AxiosMiddleware from "../../../libs/AxiosMiddleware";
import OPCODE from "../../../libs/OPCODE";
import router from "../../../router";

export default {
  name: "AdminUsers",
  components: { TheAdmin },
  data() {
    this.onLoad();
    return {
      isLoading: false,
      statusLoading: false,
      amountDiff: 0,
      details: { balance: {}, business: {} },
      account: {
        username: "",
        identity: "",
        email: "",
        phone: "",
        balance: {
          current: 0,
          price: 0,
          paymentDate: this.$moment().add("30", "day"),
          paymentType: PaymentType.MONTHLY,
          shipping: 2500
        },
        business: {
          name: "",
          phone: "",
          address: "",
          homepage: ""
        },
        apply: {
          always: false,
          startTime: this.$moment(0),
          endTime: this.$moment(0)
        },
        locations: []
      },
      locations: []
    };
  },
  methods: {
    async onLoad() {
      const { data } = await Axios.get(
        `${config.hostname}/v1/admin/users/locations`,
        {
          withCredentials: true
        }
      );

      if (AxiosMiddleware(data.opcode)) {
        switch (data.opcode) {
          case OPCODE.SUCCESS:
            this.locations = data.locations;
            break;
        }
      }
    },
    async onApply() {
      this.isLoading = true;
      this.details = { balance: {}, business: {} };
      try {
        const { data } = await Axios.post(
          `${config.hostname}/v1/admin/users/`,
          {
            username: this.account.username,
            identity: this.account.identity,
            email: this.account.email,
            phone: this.account.phone,
            balance: {
              current: this.account.balance.current,
              price: this.account.balance.price,
              paymentDate: this.account.balance.paymentDate,
              paymentType: this.account.balance.paymentType,
              shipping: this.account.balance.shipping
            },
            business: {
              name: this.account.business.name,
              phone: this.account.business.phone,
              address: this.account.business.address,
              homepage: this.account.business.homepage
            },
            apply: {
              always: this.account.apply.always,
              startTime: this.account.apply.startTime,
              endTime: this.account.apply.endTime
            },
            locations: this.account.locations
          },
          { withCredentials: true }
        );

        if (AxiosMiddleware(data.opcode)) {
          switch (data.opcode) {
            case OPCODE.SUCCESS:
              router.push(`/admin/users/${data.idx}`);
              config.alert.mixin({
                type: "success",
                title: "성공적으로 추가 되었습니다."
              });
              break;
            case OPCODE.EXISTS:
              config.alert.mixin({
                type: "warning",
                title: "해당 아이디 또는 전화번호, 이메일은 사용중입니다."
              });
              break;
            case OPCODE.INVALID:
              this.details = Object.assign(
                { balance: {}, business: {} },
                data.details
              );

              config.alert.mixin({
                type: "warning",
                title: "오류를 확인해주세요."
              });
              break;
            default:
              config.alert.mixin({
                type: "error",
                title: "알 수 없는 이유로 추가할 수 없습니다."
              });
          }
        }
      } catch (err) {
        config.alert.mixin({
          type: "error",
          title: "추가할 수 없습니다.\n인터넷 상태를 확인해주세요."
        });
      }
      this.isLoading = false;
    },
    getMask(type) {
      let num = this.account[type].replace(/[^0-9]/g, "");

      let res = "";
      if (num.length < 3) {
        res = num;
      } else {
        if (num.substr(0, 2) == "02") {
          if (num.length <= 5) {
            //02-123-5678
            res = num.substr(0, 2) + "-" + num.substr(2, 3);
          } else if (num.length > 5 && num.length <= 9) {
            //02-123-5678
            res =
              num.substr(0, 2) + "-" + num.substr(2, 3) + "-" + num.substr(5);
          } else if (num.length > 9) {
            //02-1234-5678
            res =
              num.substr(0, 2) + "-" + num.substr(2, 4) + "-" + num.substr(6);
          }
        } else {
          if (num.length < 8) {
            res = num;
          } else if (num.length == 8) {
            res = num.substr(0, 4) + "-" + num.substr(4);
          } else if (num.length == 9) {
            res =
              num.substr(0, 3) + "-" + num.substr(3, 3) + "-" + num.substr(6);
          } else if (num.length == 10) {
            res =
              num.substr(0, 3) + "-" + num.substr(3, 3) + "-" + num.substr(6);
          } else if (num.length > 10) {
            //010-1234-5678
            res =
              num.substr(0, 3) + "-" + num.substr(3, 4) + "-" + num.substr(7);
          }
        }
      }

      this.account[type] = res;
    },
    getBusinessMask(type) {
      let num = this.account[type].replace(/[^0-9]/g, "");

      let res = "";
      if (num.length < 3) {
        res = num;
      } else {
        if (num.substr(0, 2) == "02") {
          if (num.length <= 5) {
            //02-123-5678
            res = num.substr(0, 2) + "-" + num.substr(2, 3);
          } else if (num.length > 5 && num.length <= 9) {
            //02-123-5678
            res =
              num.substr(0, 2) + "-" + num.substr(2, 3) + "-" + num.substr(5);
          } else if (num.length > 9) {
            //02-1234-5678
            res =
              num.substr(0, 2) + "-" + num.substr(2, 4) + "-" + num.substr(6);
          }
        } else {
          if (num.length < 8) {
            res = num;
          } else if (num.length == 8) {
            res = num.substr(0, 4) + "-" + num.substr(4);
          } else if (num.length == 9) {
            res =
              num.substr(0, 3) + "-" + num.substr(3, 3) + "-" + num.substr(6);
          } else if (num.length == 10) {
            res =
              num.substr(0, 3) + "-" + num.substr(3, 3) + "-" + num.substr(6);
          } else if (num.length > 10) {
            //010-1234-5678
            res =
              num.substr(0, 3) + "-" + num.substr(3, 4) + "-" + num.substr(7);
          }
        }
      }

      this.account.business[type] = res;
    },
    updateAmountDiff(isPlus) {
      if (this.amountDiff === 0) return;
      const amount = parseInt(this.account.balance.current);
      if (isPlus) {
        this.account.balance.current = amount + this.amountDiff;
      } else {
        this.account.balance.current = amount - this.amountDiff;
      }
      this.amountDiff = 0;
    }
  }
};
</script>
<style>
.mt {
  margin-top: 20px;
}
</style>